import { safeishGetProperty } from "@rootreeweb/js-lib";

import Co2 from "@/assets/icon-co2_cloud.svg";
import DirtHand from "@/assets/icon-dirt_hand.svg";
import DumbSeagull from "@/assets/icon-dumb_seagull.svg";
import MixedForest from "@/assets/icon-mixed_forest.svg";
import Kelp from "@/assets/icon-ocean_kelp.svg";
import type { CompanyDataResponse, RawForestType } from "./types/company.js";
import { impacts } from "./types/processed.js";
import type { Impact, Impacts } from "./types/processed.js";

type DescriptionMapping<T extends string> = Record<
	string,
	Pick<Impact, "icon" | "primary" | "secondary"> & {
		countKey: T;
		primaryConversion?: (value: number) => number;
		units?: string;
		valueFiller: (
			raw: CompanyDataResponse["data"]["impacts"],
			trees_by_forest_types: RawForestType[],
		) => number;
	}
>;

export const descriptionMap: {
	impacts: DescriptionMapping<keyof CompanyDataResponse["data"]["impacts"]>;
	treeTypes: DescriptionMapping<keyof RawForestType>;
} = {
	impacts: {
		areaPlanted: {
			countKey: "area_planted_ordered_projected_stat",
			icon: MixedForest,
			primary: "Hectares Reforested",
			secondary: "Provides ${0} work days to local tree planters.",
			valueFiller: (raw) => raw.working_days_projected_stat || 0,
		},
		co2: {
			countKey: "co2_ordered_projected_stat",
			icon: Co2,
			primary: "Tonnes of CO2 to be sequestered",
			secondary: "Compares to removing ${0} cars from the road for one year",
			valueFiller: (raw) => (raw.co2_ordered_projected_stat || 0) / 4.598,
		},
		oceanAreaPlanted: {
			countKey: "ocean_area_planted_ordered_projected_stat",
			icon: Kelp,
			primary: `Ocean Area Restored`,
			primaryConversion: (value: number) => value * 10000,
			secondary: `Equivalent to the area approximately the size of 0 parking space`,
			units: "m²",
			valueFiller: (raw) =>
				((raw.ocean_area_planted_ordered_projected_stat || 0) * 10000) / 4.55,
		},
		yield: {
			countKey: "yield_ordered_projected_stat",
			icon: DumbSeagull,
			primary: "Estimated Yield",
			secondary: "Equivalent to the weight of ${0} seagulls",
			units: "kg",
			valueFiller: (raw) => (raw.yield_ordered_projected_stat || 0) / 4,
		},
	},
	treeTypes: {
		Trees: {
			countKey: "trees_ordered",
			icon: DirtHand,
			primary: "Total Trees",
			secondary: "Compares to planting ${0} studio apartments full of trees",
			// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
			valueFiller: (_, trees) => (trees[1]?.trees_ordered || 0) / 11.22,
		},
	},
};

/**
 * Converts the raw impacts data into a more usable format.
 *
 * @param raw The raw impacts data.
 * @param trees_by_forest_types The raw forest types data.
 * @returns The converted impacts data.
 */
export function convertImpacts(
	raw: CompanyDataResponse["data"]["impacts"],
	trees_by_forest_types: RawForestType[],
): Impacts {
	const base: Impacts = [];
	for (const forestType in descriptionMap.treeTypes) {
		const treeTypeData = processTreeType(
			forestType,
			raw,
			trees_by_forest_types,
		);
		if (!treeTypeData) continue;
		base.push(treeTypeData);
	}

	for (const impact in descriptionMap.impacts) {
		const impactData = processImpact(impact, raw, trees_by_forest_types);
		if (!impactData) continue;
		base.push(impactData);
	}

	return base;
}

/**
 * Adjusts the counts and text for a given impact.
 *
 * @template T The type of the count key.
 * @param rawCount The raw count.
 * @param supplementary The supplementary data for the impact.
 * @param impactData The impact data.
 * @param overallData The overall data.
 * @returns The adjusted counts and text.
 */
function adjustCountsAndText<T extends string>(
	rawCount: number,
	supplementary: DescriptionMapping<T>[keyof DescriptionMapping<T>],
	impactData: CompanyDataResponse["data"]["impacts"],
	overallData: RawForestType[],
) {
	let count = rawCount;
	if (supplementary.primaryConversion) {
		count = supplementary.primaryConversion(count);
	}
	const secondaryValue = supplementary.valueFiller(impactData, overallData);

	return {
		count,
		secondary: supplementary.secondary,
		secondaryValue: secondaryValue.toLocaleString(),
	};
}

/**
 * Builds an Impact, only including `units` when it's actually set — required
 * under `exactOptionalPropertyTypes`, since `units` is optional but not
 * nullable.
 *
 * @param impact The impact fields, with `units` possibly undefined.
 * @returns The built Impact.
 */
function buildImpact(
	impact: Omit<Impact, "units"> & { units?: string | undefined },
): Impact {
	const { units, ...rest } = impact;
	return units === undefined ? rest : { ...rest, units };
}

/**
 * Converts a raw impact into a more usable format.
 *
 * @param impact The impact to convert.
 * @param raw The raw impacts data.
 * @param trees_by_forest_types The raw forest types data.
 * @returns The converted impact data.
 * @throws {Error} If no supplementary data is found for the impact.
 */
function processImpact(
	impact: string,
	raw: CompanyDataResponse["data"]["impacts"],
	trees_by_forest_types: RawForestType[],
): Impact | null {
	const slug = impact as keyof typeof impacts;
	if (!(slug in impacts)) {
		return null;
	}
	const supplementary = safeishGetProperty(descriptionMap.impacts, slug);
	let count = (raw[supplementary.countKey] as number) || 0;
	const {
		count: newCount,
		secondary,
		secondaryValue,
	} = adjustCountsAndText(count, supplementary, raw, trees_by_forest_types);
	count = newCount;
	return buildImpact({
		count,
		icon: supplementary.icon,
		primary: supplementary.primary,
		secondary,
		secondaryValue,
		slug,
		units: supplementary.units,
	});
}

/**
 * Converts a tree type into a more usable format.
 *
 * @param forestType The forest type to convert.
 * @param raw The raw impacts data.
 * @param trees_by_forest_types The raw forest types data.
 * @returns The converted tree type data.
 */
function processTreeType(
	forestType: string,
	raw: CompanyDataResponse["data"]["impacts"],
	trees_by_forest_types: RawForestType[],
): Impact | null {
	if (!(forestType in descriptionMap.treeTypes)) {
		return null;
	}
	const supplementary = safeishGetProperty(
		descriptionMap.treeTypes,
		forestType,
	);
	const matching_forest_types = trees_by_forest_types.filter(
		(forest) => forest.type === forestType,
	);

	if (matching_forest_types.length === 0) return null;

	const totalCount = matching_forest_types.reduce((sum, forest) => {
		const count = (forest[supplementary.countKey] as number) || 0;
		return sum + count;
	}, 0);

	let count = totalCount;
	const {
		count: newCount,
		secondary,
		secondaryValue,
	} = adjustCountsAndText(count, supplementary, raw, trees_by_forest_types);
	count = newCount;

	const slug = matching_forest_types
		.map((forest) => forest.forest_type_id)
		.join(",");

	return buildImpact({
		count,
		icon: supplementary.icon,
		primary: supplementary.primary,
		secondary,
		secondaryValue,
		slug,
		units: supplementary.units,
	});
}
